<template>
  <tr :class="payment.status">
    <td
      :class="payment.status"
      class="row-num"
    >
      <div class="spacer">
        <div class="white-background" />
        <span
          class="circle-number"
          v-html="formatInstallmentNumber(payment.installment_number)"
        />
      </div>
    </td>
    <td
      :class="payment.status"
      class="installment-due-date-cell"
    >
      <div
        class="spacer"
      >
        <p
          class="installment-due-date"
          v-html="formatDate(payment.installment_due_date)"
        />
        <p
          v-if="payment.status === 'next'"
          id="next-payment-message"
          v-html="translate('next_payment_message')"
        />
      </div>
    </td>
    <td
      :class="payment.status"
      class="installment-amount"
    >
      <div class="spacer">
        <template v-if="payment.status === 'paid_off'">
          <hr>
        </template>
        <template v-else>
          {{ formatInstallmentAmount(payment.installment_amount, payment.status) }}
        </template>
      </div>
    </td>
    <td
      :class="payment.status"
    >
      <div
        class="spacer"
      >
        <template v-if="payment.status === 'defaulted'">
          {{ formatCurrentDebt(payment.outstanding_amount) }}
        </template>
      </div>
    </td>
    <td
      :class="payment.status"
    >
      <div
        class="spacer"
      >
        <template
          v-if="payment.status === 'under_payment_plan'"
        >
          <span
            class="payment-plan-link"
            @click="onPaymentPlanClicked(payment.payment_plan_id, payment.installment_number)"
          >
            <img
              src="~images/svg/MagnifyingGlassDocument.svg"
              class="open-payment-plan-icon"
              alt="Payment Plan Details"
            >
            {{ formatStatus(payment) }}
          </span>
        </template>
        <template v-else>
          {{ formatStatus(payment) }}
        </template>
      </div>
    </td>
  </tr>
</template>
<script>
import { format } from 'date-fns-tz';
import brazilDateFormat from '../filters/brazilDateFormat';
import currencyReals from '../filters/currencyReals';

export default {
  props: {
    payment: {
      type:     Object,
      required: true,
    },
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      const utc = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      const formattedDateString = format(utc, 'yyyy-MM-dd');
      const options = { delimiter: '/' };
      return brazilDateFormat(formattedDateString, options);
    },
    formatInstallmentAmount(amount, status) {
      if (status === 'paid_off') {
        return '<hr class="no-value" />';
      }
      return currencyReals(amount);
    },
    formatCurrentDebt(amount) {
      if (typeof amount === 'undefined' || amount === null) {
        return '';
      }
      return currencyReals(amount);
    },
    formatStatus(payment) {
      switch (payment.status) {
        case 'paid_off':
          return this.translate('paid_off');
        case 'under_payment_plan':
          return this.translate('under_payment_plan') + this.activePaymentPlanSuffix(payment.active_payment_plan_number);
        case 'defaulted':
          return this.translate('defaulted');
        case 'pending':
          return this.translate('pending');
        case 'unpaid':
          return this.translate('pending');
        case 'next':
          return this.translate('pending');
        default:
          return '';
      }
    },
    formatInstallmentNumber(numString) {
      return Number(numString);
    },
    translate(key) {
      return this.$t(`components.loan_status_table_row.${key}`);
    },
    onPaymentPlanClicked(paymentPlanId, installmentNumber) {
      this.$emit('openPaymentPlan', paymentPlanId, installmentNumber);
    },
    activePaymentPlanSuffix(int) {
      if (typeof int === 'undefined' || int === null || int < 1 || int > 26) {
        return '';
      }
      return ` - ${String.fromCharCode(96 + int).toUpperCase()}`; // A, B, C ...
    },
  },
};
</script>
<style lang="scss" scoped>
  tbody {
    tr:last-child .row-num:after {
      content: none;
    }
    td {
      border-right: 1px solid rgb(238, 238, 238);
    }
    hr {
      height: 2px;
      background-color: rgb(116, 119, 124);
      margin: 0 auto;
      width: 30%;
    }
  }
  td {
    text-align: center;
    font-weight: bold;
    position: relative;
    background-color: white;
    .circle-number {
      box-sizing: content-box;
      border-radius: 50%;
      text-align: center;
      font-size: 1.5em;
      width: 2.5em;
      height: 2.5em;
      line-height: 2em;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    .spacer {
      margin-top: 1em;
      margin-bottom: 1em;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      height: 3.5em;
    }
    .installment-due-date {
      margin: 0;
    }
    &.row-num {
      &:after {
        content: '';
        height: 100%;
        display: block;
        margin-left: 50%;
        position: absolute;
        z-index: 3;
        margin-top: -50%;
      }
      div.white-background {
        background-color: white;
        margin-right: 50%;
        position: absolute;
        width: 50%;
        height: 3.5em;
        z-index: 2;
      }
      .circle-number {
        margin-right: -50%;
        z-index: 4;
        position: relative;
        margin: 0 auto;
      }
    }
    &.paid_off {
      color: rgb(56, 125, 35);
      .circle-number {
        color: white;
        background-color: rgb(56, 125, 35);
      }
      &:after {
        border-right: 2px solid rgb(56, 125, 35);
      }
    }
    &.under_payment_plan {
      font-weight: bold;
      &.installment-amount, &.installment-due-date-cell {
        text-decoration: line-through;
      }
      .spacer {
        background-color: rgb(230, 239, 252);
        .circle-number {
          color: white;
          background-color: rgb(45, 107, 148);
        }
      }
      &:after {
        border-right: 2px solid rgb(45, 107, 148);
      }
    }
    &.defaulted {
      font-weight: bold;
      .spacer {
        background-color: rgb(248, 232, 230);
        .circle-number {
          color: white;
          background-color: rgb(196, 41, 28);
        }
      }
      &:after {
        border-right: 2px solid rgb(196, 41, 28);
      }
    }
    &.next {
      font-weight: bold;
      .spacer {
        display: block;
        padding: 0 1em;
        .circle-number {
        color: black;
        background-color: rgb(237, 193, 66);
        }
      }
      &:after {
        border-right: 2px solid rgb(237, 193, 66);
      }
    }
    &.pending, &.unpaid {
      color: rgb(116, 119, 124);
      .spacer {
        .circle-number {
          color: white;
          background-color: rgb(116, 119, 124);
        }
      }
      &:after {
        border-right: 2px solid rgb(116, 119, 124);
      }
    }
  }
  .payment-plan-link {
    color: rgb(45, 107, 148);
    font-size: 1.1em;
    &:hover {
      cursor: pointer;
    }
  }
  #next-payment-message {
    font-weight: 200;
    font-size: 0.75em;
    line-height: 1.1;
  }
  .open-payment-plan-icon {
    height: 1em;
  }
</style>
